import * as React from "react";
import { Avatar } from "@/components/ui/avatar";
import { API_BASE_URL } from "@/lib/api";
import { cn } from "@/lib/utils";

interface UserAvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Profile image URL from the backend (e.g., "/storage/profile_images/...")
   * Can be null/undefined to show fallback
   */
  profileImageUrl?: string | null;
  /**
   * User's name for generating initials fallback
   */
  name: string;
  /**
   * Alt text for the image (defaults to name)
   */
  alt?: string;
  /**
   * Avatar size
   */
  size?: "xs" | "sm" | "default" | "lg";
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * UserAvatar - Professional avatar component for displaying user profile images
 *
 * Features:
 * - Automatically constructs correct image URL from backend path
 * - Graceful fallback to initials when no image is available
 * - Handles image loading errors
 * - Responsive sizing (xs, sm, default, lg)
 * - Type-safe with proper TypeScript interfaces
 *
 * Usage:
 * ```tsx
 * <UserAvatar
 *   profileImageUrl={user.profile_image_url}
 *   name={user.name}
 *   size="sm"
 * />
 * ```
 *
 * @example
 * // With profile image
 * <UserAvatar
 *   profileImageUrl="/storage/profile_images/user-123.jpg"
 *   name="João Silva"
 * />
 *
 * @example
 * // Without image (shows initials)
 * <UserAvatar
 *   profileImageUrl={null}
 *   name="Maria Santos"
 *   size="lg"
 * />
 */
export function UserAvatar({
  profileImageUrl,
  name,
  alt,
  size = "default",
  className,
  ref,
  ...props
}: UserAvatarProps & { ref?: React.Ref<HTMLDivElement> }) {
  // As imagens são servidas fora de `/api/v1` (em `/storage/…`), por isso
  // tira-se o sufixo à base. `API_BASE_URL` é relativo (`/api/v1`), o que
  // deixa o caminho na mesma origem da app — e, com dois clientes na mesma
  // máquina, na origem do cliente CERTO.
  const imageSrc = React.useMemo(() => {
    if (!profileImageUrl) return undefined;
    const baseUrl = API_BASE_URL.replace("/api/v1", "");
    return `${baseUrl}${profileImageUrl}`;
  }, [profileImageUrl]);

  return (
    <Avatar
      ref={ref}
      src={imageSrc}
      alt={alt || name}
      fallback={name}
      size={size}
      className={cn(className)}
      {...props}
    />
  );
}
